import { CompanyId } from "@custom-types/base-types";
import { BaseService } from "@services/integrations-service/base-service";
import { INTEGRATION_ERROR_CODES } from "@services/integrations-service/integrations-service-constants";
import {
  IntegrationCompany,
  IntegrationProject,
  ProcoreLegacyMap,
  ProjectIntegrationsMap,
} from "@services/integrations-service/integrations-service-types";
import { isProcoreLegacyProjectIntegration } from "@services/integrations-service/integrations-type-guards";
import { FaroCoreApiUtils, FaroCoreApiTypes } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";

/** Service that provides methods to manage the Procore 3rd party integration */
export class ProcoreService extends BaseService {
  protected integrationId: FaroCoreApiTypes.IntegrationId = "procore";

  /**
   * @returns the IntegrationCompany entities of the user
   * @throws {Error} if it fails to get the entities
   */
  public async getIntegrationCompanies(): Promise<IntegrationCompany[]> {
    const proxyRequest = this.coreApiClient.integration.getProcoreCompanies;
    const procoreCompanies = await this.request(proxyRequest);

    return procoreCompanies.map((procoreCompany) => ({
      integrationId: "procore",
      id: procoreCompany.id.toString(),
      name: procoreCompany.name,
    }));
  }

  /**
   * @returns the IntegrationProject entities for a given integration company
   * @throws {Error} if it fails to get the entities
   * @param companyId ID of the company
   */
  public async getIntegrationProjects(
    companyId: CompanyId
  ): Promise<IntegrationProject[]> {
    const proxyRequest = (): Promise<FaroCoreApiTypes.ProcoreEntity[]> =>
      this.coreApiClient.integration.getProcoreProjects(companyId);
    const procoreProjects = await this.request(proxyRequest);

    const integrationProjects: IntegrationProject[] = procoreProjects.map(
      (procoreProject) => {
        const bcfProjectId = `${procoreProject.id}~${companyId}`;
        return {
          integrationId: "procore",
          id: procoreProject.id.toString(),
          name: procoreProject.name,
          companyId,
          rfisBcfProjectId: bcfProjectId,
          issuesOrObservationsBcfProjectId: bcfProjectId,
          type: "none",
        };
      }
    );

    return integrationProjects;
  }

  /**
   * Handles errors originating from the CoreAPI proxy backend for requests issued to the Procore backend.
   * - If the error status is UNAUTHORIZED it attempts to update the token.
   * - If the error status is FORBIDDEN and for some specific error codes then it also attempts
   * to update the token.
   * @param error Error to handle
   * @throws {HttpUtils.FetchError} if it can't handle the error.
   */
  protected async handleIntegrationError(
    error: HttpUtils.FetchError
  ): Promise<void> {
    switch (error.status) {
      case HttpUtils.HTTP_ERROR_CODE.Unauthorized:
        await this.handleTokenUpdate();
        break;
      case HttpUtils.HTTP_ERROR_CODE.Forbidden:
        if (
          // Check if the error body contains a raw Core API response error
          FaroCoreApiUtils.Client.isResponseError(error.errorBody) &&
          (error.errorBody.error_v2 ===
            INTEGRATION_ERROR_CODES.authentication_error ||
            error.errorBody.error_v2 ===
              INTEGRATION_ERROR_CODES.no_authorization_token)
        ) {
          await this.handleTokenUpdate();
        } else {
          throw error;
        }
        break;
      default:
        throw error;
    }
  }

  /**
   * Updates the project connection data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param data Project integrations map
   */
  public updateDataFormat(
    data: ProcoreLegacyMap
  ): Promise<ProjectIntegrationsMap> {
    const bcfProjectId = `${data.procore.projectId}~${data.procore.companyId}`;

    return Promise.resolve({
      ...data,
      ["procore.observations"]: {
        bcfProjectId,
      },
      ["procore.rfis"]: {
        bcfProjectId,
      },
    });
  }

  /**
   * @returns Whether the project connection data format is of type Legacy (HB).
   * The legacy Procore data format can be identified from the project integrations map when
   * the "procore" property is defined but the other 2 new Sphere XG Procore fields are undefined or null.
   * @param data Project integrations map
   */
  public isLegacyDataFormat(
    data: ProjectIntegrationsMap
  ): data is ProcoreLegacyMap {
    return (
      isProcoreLegacyProjectIntegration(data.procore) &&
      !data["procore.observations"] &&
      !data["procore.rfis"]
    );
  }
}
